import { StateObservable } from 'redux-observable';
import { Observable } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';

import { IOrganization } from '@src/model/organization/Organization';
import EntityApiServiceRegistry from '@src/service/api/registry/entity/EntityApiServiceRegistry';
import ListFilterBusinessStore from '@src/service/business/common/listFilterBusinessStore';
import { ICollectionData, ICollectionFetchPayload, ILemonAction, IPayloadAction } from '@src/service/business/common/types';
import { createApiResponseUserFeedbackError } from '@src/service/business/common/userFeedbackUtils';
import { startGlobalProgress, stopGlobalProgress, trackAction } from '@src/service/util/observable/operators';
import { reportCaughtMessage } from '@src/service/util/observable/operators/userFeedback';

// -
// -------------------- Types&Consts
export interface IOrganizationListFilter {
  name?: string;
  member?: string;
}

// List filter ID
const ORGANIZATION_LIST_FILTER = '@@ORGANIZATION_LIST_FILTER';

// -
// -------------------- Selectors

/** Returns Organization list from store. */
const getOrganizationList = (store: any): ICollectionData<IOrganization> => store.organizationList;

/** Returns Organization list filter. */
const getOrganizationListFilter = (store: any): IOrganizationListFilter => ListFilterBusinessStore.selectors.getListFilter(store, ORGANIZATION_LIST_FILTER);

// -
// -------------------- Actions
const Actions = {
  ORGANIZATION_LIST_FETCH: 'ORGANIZATION_LIST_FETCH',
  ORGANIZATION_LIST_LOAD: 'ORGANIZATION_LIST_LOAD',
  ORGANIZATION_LIST_CLEAR: 'ORGANIZATION_LIST_CLEAR',
};

/** Fetch Organization list by filter */
const fetchOrganizationList = (params: ICollectionFetchPayload<IOrganizationListFilter>): IPayloadAction<ICollectionFetchPayload<IOrganizationListFilter>> => {
  return {
    type: Actions.ORGANIZATION_LIST_FETCH,
    payload: params,
  };
};

/** Load Organization list to the store */
const loadOrganizationList = (data: ICollectionData<IOrganization>): IPayloadAction<ICollectionData<IOrganization>> => {
  return {
    type: Actions.ORGANIZATION_LIST_LOAD,
    payload: data,
  };
};

/** Clear Organization list from store. Eg. when leaving view. */
const clearOrganizationList = (): ILemonAction => {
  return {
    type: Actions.ORGANIZATION_LIST_CLEAR,
  };
};

/** Store list filter to store. */
const storeOrganizationListFilter = (listFilter: IOrganizationListFilter): ILemonAction => {
  return ListFilterBusinessStore.actions.storeListFilter(ORGANIZATION_LIST_FILTER, listFilter);
};

/** Clear list filter from store. */
const clearOrganizationListFilter = (): ILemonAction => {
  return ListFilterBusinessStore.actions.clearListFilter(ORGANIZATION_LIST_FILTER);
};

// -
// -------------------- Side-effects

const fetchOrganizationListEffect = (action$: Observable<IPayloadAction<ICollectionFetchPayload<IOrganizationListFilter>>>, state$: StateObservable<any>) => {
  return action$.pipe(
    filter((action) => {
      return action.type === Actions.ORGANIZATION_LIST_FETCH;
    }),

    startGlobalProgress(),

    mergeMap((action) => {
      const payload = action.payload;

      return EntityApiServiceRegistry.getService('OrganizationProfile')
        .fetchEntityList(payload)
        .pipe(trackAction(action));
    }),

    stopGlobalProgress(),

    map((data) => {
      return loadOrganizationList(data);
    }),

    reportCaughtMessage((error: any) => createApiResponseUserFeedbackError(error, 'ORGANIZATION.ERROR', 'GENERAL_MESSAGE.GENERAL_FETCH_ERROR')),

    catchError((error: any, o: Observable<any>) => {
      console.log('Error fetching Organization list', error);
      return o;
    })
  );
};

// -
// -------------------- Reducers
const organizationList = (state: ICollectionData<IOrganization> | null = null, action: IPayloadAction<ICollectionData<IOrganization>>) => {
  if (action.type === Actions.ORGANIZATION_LIST_LOAD) {
    return {
      ...action.payload,
    };
  } else if (action.type === Actions.ORGANIZATION_LIST_CLEAR) {
    return null;
  }

  return state;
};

// --
// -------------------- Business Store

export const OrganizationListBusinessStore = {
  actions: {
    fetchOrganizationList,
    loadOrganizationList,
    clearOrganizationList,
    storeOrganizationListFilter,
    clearOrganizationListFilter,
  },

  selectors: {
    getOrganizationList,
    getOrganizationListFilter,
  },

  effects: {
    fetchOrganizationListEffect,
  },

  reducers: {
    organizationList,
  },
};

// --
// export business store

export default OrganizationListBusinessStore;
